import React, { useCallback, useEffect, useState } from 'react';
import { Button, Dropdown, Heading, Loader, Text, Toggle } from '@vibe/core';
import {
  getTimesheetConfig,
  updateTimesheetConfig,
} from '../../api/timesheet-approval.api';
import type { TimesheetConfig } from '../../types/timesheet-approval.types';

const PERIOD_OPTIONS = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
];

const WEEK_START_OPTIONS = [
  { value: 'monday', label: 'Monday' },
  { value: 'sunday', label: 'Sunday' },
  { value: 'saturday', label: 'Saturday' },
];

const TimesheetConfigPanel: React.FC = () => {
  const [config, setConfig] = useState<TimesheetConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // Local form state
  const [approvalEnabled, setApprovalEnabled] = useState(true);
  const [periodType, setPeriodType] = useState('weekly');
  const [weekStartDay, setWeekStartDay] = useState('monday');
  const [lockOnApproval, setLockOnApproval] = useState(true);

  useEffect(() => {
    setIsLoading(true);
    getTimesheetConfig()
      .then((cfg) => {
        setConfig(cfg);
        setApprovalEnabled(cfg.approval_enabled);
        setPeriodType(cfg.period_type);
        setWeekStartDay(cfg.week_start_day);
        setLockOnApproval(cfg.lock_on_approval);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Failed to load config');
      })
      .finally(() => setIsLoading(false));
  }, []);

  const hasChanges =
    config &&
    (approvalEnabled !== config.approval_enabled ||
      periodType !== config.period_type ||
      weekStartDay !== config.week_start_day ||
      lockOnApproval !== config.lock_on_approval);

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    setError(null);
    setSaved(false);
    try {
      const updated = await updateTimesheetConfig({
        approval_enabled: approvalEnabled,
        period_type: periodType,
        week_start_day: weekStartDay,
        lock_on_approval: lockOnApproval,
      });
      setConfig(updated);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save config');
    } finally {
      setIsSaving(false);
    }
  }, [approvalEnabled, periodType, weekStartDay, lockOnApproval]);

  if (isLoading) {
    return (
      <div className="rp-workload-defaults">
        <div className="rp-workload-defaults__loading">
          <Loader size={Loader.sizes.MEDIUM} />
        </div>
      </div>
    );
  }

  return (
    <div className="rp-workload-defaults">
      <Heading type={Heading.types.H3}>Timesheet Configuration</Heading>
      <Text
        type={Text.types.TEXT2}
        style={{ color: '#676879', marginTop: 4, marginBottom: 16 }}
      >
        Configure timesheet approval workflow, submission periods, and week
        boundaries.
      </Text>

      <div className="rp-workload-defaults__fields">
        <div className="rp-workload-defaults__field rp-workload-defaults__toggle-row">
          <label className="rp-workload-defaults__label">
            Approval Workflow
          </label>
          <Toggle
            isSelected={approvalEnabled}
            onChange={(checked: boolean) => setApprovalEnabled(checked)}
            size="small"
            ariaLabel="Enable approval workflow"
          />
          <p className="rp-workload-defaults__hint">
            When enabled, timesheets must be submitted and approved before they
            are finalized.
          </p>
        </div>

        <div className="rp-workload-defaults__field">
          <label className="rp-workload-defaults__label">
            Submission Period
          </label>
          <div className="rp-workload-defaults__dropdown">
            <Dropdown
              size={Dropdown.sizes.SMALL}
              options={PERIOD_OPTIONS}
              value={PERIOD_OPTIONS.find((o) => o.value === periodType)}
              onChange={(option: { value: string } | null) => {
                if (option) setPeriodType(option.value);
              }}
              clearable={false}
            />
          </div>
          <p className="rp-workload-defaults__hint">
            How often timesheets are submitted for approval.
          </p>
        </div>

        <div className="rp-workload-defaults__field">
          <label className="rp-workload-defaults__label">
            First Day of Week
          </label>
          <div className="rp-workload-defaults__dropdown">
            <Dropdown
              size={Dropdown.sizes.SMALL}
              options={WEEK_START_OPTIONS}
              value={WEEK_START_OPTIONS.find((o) => o.value === weekStartDay)}
              onChange={(option: { value: string } | null) => {
                if (option) setWeekStartDay(option.value);
              }}
              clearable={false}
            />
          </div>
          <p className="rp-workload-defaults__hint">
            Controls the week boundary for weekly timesheet submissions.
          </p>
        </div>

        <div className="rp-workload-defaults__field rp-workload-defaults__toggle-row">
          <label className="rp-workload-defaults__label">
            Lock on Approval
          </label>
          <Toggle
            isSelected={lockOnApproval}
            onChange={(checked: boolean) => setLockOnApproval(checked)}
            size="small"
            ariaLabel="Lock timesheet on approval"
          />
          <p className="rp-workload-defaults__hint">
            When enabled, approved timesheet entries cannot be edited.
          </p>
        </div>
      </div>

      {error && <p className="rp-workload-defaults__error">{error}</p>}

      <div className="rp-workload-defaults__actions">
        <Button onClick={handleSave} loading={isSaving} disabled={!hasChanges}>
          {saved ? 'Saved!' : 'Save'}
        </Button>
      </div>
    </div>
  );
};

export default TimesheetConfigPanel;
